/* eslint-disable sort-keys */
import { ordinalSuffix } from '@lib/number';
import { invert } from 'lodash';
import { DateTime } from 'luxon';
import {
  CalendarEventStatus, CalendarEventTypes,
  ICalendarEvent,
} from '@components/shared/ScheduleCall/model/ScheduleCall';
import { IDesigner } from '@components/pages/Room/shared/model/Room';

interface IStatusMessage {
  [key: string]: string;
}

interface IPossibleMessageComplements {
  designer?: IDesigner;
  dueDate: string;
  phaseName?: string;
  status: string;
  calendarEvent: ICalendarEvent | null;
  canScheduleCall: boolean;
}

export const PROSPECT = 'PROSPECT';
export const CONSULTATION_COMPLETE = 'CONSULTATION_COMPLETE';
export const AWAITING_CONCEPT = 'AWAITING_CONCEPT';
export const CONCEPT_REVIEW = 'CONCEPT_REVIEW';
export const AWAITING_DESIGN = 'AWAITING_DESIGN';
export const DESIGN_PROCESS_COMPLETE = 'DESIGN_PROCESS_COMPLETE';
export const ON_HOLD = 'ON_HOLD';
export const CLOSED = 'CLOSED';
export const AWAITING_FINAL_CONCEPT = 'AWAITING_FINAL_CONCEPT';
export const FINAL_CONCEPT_REVIEW = 'FINAL_CONCEPT_REVIEW';
export const DESIGN_REVIEW = 'DESIGN_REVIEW';
export const ON_HOLD_FOR_CALL = 'ON_HOLD_FOR_CALL';
export const AWAITING_IDEAS = 'AWAITING_IDEAS';
export const IDEA_REVIEW = 'IDEA_REVIEW';

// Airbnb flow
export const AWAITING_IMPROVEMENTS = 'AWAITING_IMPROVEMENTS';
export const REVIEWING_IMPROVEMENTS = 'REVIEWING_IMPROVEMENTS';
export const UPLOAD_PHOTOS = 'UPLOAD_PHOTOS';
export const AWAITING_APPROVAL = 'AWAITING_APPROVAL';

// Havenly Consult Flow
export const CONSULT_READY = 'CONSULT_READY';
export const CONSULT_ACTIVE = 'CONSULT_ACTIVE';
export const CONSULT_AWAITING_BOARD = 'CONSULT_AWAITING_BOARD';
export const CONSULT_AWAITING_FEEDBACK = 'CONSULT_AWAITING_FEEDBACK';
export const CONSULT_COMPLETE = 'CONSULT_COMPLETE';
export const CONSULT_ON_HOLD = 'CONSULT_ON_HOLD';

// Havenly Core 3.0
export const AWAITING_DELIVERABLE = 'AWAITING_DELIVERABLE';
export const DELIVERABLE_REVIEW = 'DELIVERABLE_REVIEW';

// HIP
export const AWAITING_VISION_BOARD = 'AWAITING_VISION_BOARD';
export const VISION_BOARD_REVIEW = 'VISION_BOARD_REVIEW';
export const AWAITING_DESIGN_PROPOSAL = 'AWAITING_DESIGN_PROPOSAL';
export const DESIGN_PROPOSAL_REVIEW = 'DESIGN_PROPOSAL_REVIEW';
export const AWAITING_3D_DESIGN = 'AWAITING_3D_DESIGN';
export const THREED_DESIGN_REVIEW = '3D_DESIGN_REVIEW';

export const roomStatusesById: KeyedObject = {
  1: PROSPECT,
  2: CONSULTATION_COMPLETE,
  3: AWAITING_CONCEPT,
  4: CONCEPT_REVIEW,
  7: AWAITING_DESIGN,
  8: DESIGN_PROCESS_COMPLETE,
  11: ON_HOLD,
  12: CLOSED,
  13: AWAITING_FINAL_CONCEPT,
  14: FINAL_CONCEPT_REVIEW,
  15: AWAITING_IMPROVEMENTS,
  16: REVIEWING_IMPROVEMENTS,
  17: UPLOAD_PHOTOS,
  18: AWAITING_APPROVAL,
  19: AWAITING_IDEAS,
  20: IDEA_REVIEW,
  21: CONSULT_READY,
  22: CONSULT_ACTIVE,
  23: CONSULT_AWAITING_BOARD,
  24: CONSULT_AWAITING_FEEDBACK,
  25: CONSULT_COMPLETE,
  26: CONSULT_ON_HOLD,
  27: ON_HOLD_FOR_CALL,
  28: DESIGN_REVIEW,
  30: AWAITING_DELIVERABLE,
  31: DELIVERABLE_REVIEW,
  32: AWAITING_VISION_BOARD,
  33: VISION_BOARD_REVIEW,
  34: AWAITING_DESIGN_PROPOSAL,
  35: DESIGN_PROPOSAL_REVIEW,
  36: AWAITING_3D_DESIGN,
  37: THREED_DESIGN_REVIEW
};

export const roomStatusesByName = invert(roomStatusesById);

const getMessageFromEvent = (
  designer?: IDesigner,
  calendarEvent?: ICalendarEvent | null,
  formattedDate?: string,
) => {
  if (!designer || !formattedDate) {
    return '';
  }

  if (!calendarEvent || calendarEvent.status === CalendarEventStatus.CANCELED) {
    return `${designer.firstName} will start ${formattedDate}`;
  }

  const startTime = DateTime.fromISO(calendarEvent.startTime, {
    zone: designer.timezone
  });

  return `Your next meeting is scheduled for ${startTime.toFormat('D')}.
          Click ‘My Meetings’ for more info and to access the call link.`;
};

export const roomStatusMessageByName = ({
  designer,
  dueDate,
  phaseName = '',
  status,
  calendarEvent,
  canScheduleCall
}: IPossibleMessageComplements): string => {
  let formattedDate;

  if (dueDate) {
    const date1: any = new Date();
    date1.setUTCHours(0, 0, 0, 0);
    const date2: any = new Date(dueDate.split('T')[0]);
    date2.setUTCHours(0, 0, 0, 0);
    const diffTime = date2 - date1;
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (days <= 0) {
      formattedDate = 'today';
    } else if (days === 1) {
      formattedDate = 'tomorrow';
    } else {
      const parsedDueDate = DateTime.fromISO(dueDate.split('T')[0]);
      formattedDate = `on ${parsedDueDate.toFormat('MMMM d')}${ordinalSuffix(parsedDueDate.day)}`;
    }
  }

  const isKickoffCall = canScheduleCall ||
    (calendarEvent?.eventType.id === CalendarEventTypes.KICKOFF_CALL
      || calendarEvent?.eventType.id === CalendarEventTypes.DESIGN_REVIEW);

  const hasDesignReviewCallScheduled =
    calendarEvent?.eventType.id === CalendarEventTypes.DESIGN_REVIEW;

  const messages: IStatusMessage = {
    PROSPECT: '',
    CONSULTATION_COMPLETE: `${designer?.firstName} will be with you ${formattedDate || 'in over a month'}`,
    AWAITING_IDEAS: isKickoffCall
      ? getMessageFromEvent(designer, calendarEvent, formattedDate)
      : `It's happening!  Your design ideas will be ready ${formattedDate}`,
    IDEA_REVIEW: isKickoffCall
      ? getMessageFromEvent(designer, calendarEvent, formattedDate)
      : `We need your help! Ready to give ${designer?.firstName} your feedback?`,
    AWAITING_CONCEPT: `Get ready! Your design concept will be ready ${formattedDate}`,
    CONCEPT_REVIEW: `You got this! Give ${designer?.firstName} feedback on your concept!`,
    AWAITING_DESIGN: `Wait for it...Your design will be ready to review ${formattedDate}`,
    AWAITING_FINAL_CONCEPT: `Your final design will be ready to review ${formattedDate}`,
    DESIGN_REVIEW: hasDesignReviewCallScheduled
      ? `So what do you think? Give ${designer?.firstName} your feedback!`
      : `So what do you think? Give ${designer?.firstName} feedback on your design in chat or schedule time together!`,
    FINAL_CONCEPT_REVIEW: `So what do you think? Give ${designer?.firstName} your feedback!`,
    AWAITING_IMPROVEMENTS: '',
    REVIEWING_IMPROVEMENTS: '',
    DESIGN_PROCESS_COMPLETE: `Ready to make it happen? Shop your designer curated products.`,
    ON_HOLD: '',
    CLOSED: '',
    UPLOAD_PHOTOS: '',
    AWAITING_APPROVAL: '',
    OVERDUE_DELIVERABLE: `${phaseName} coming soon!`,
  };

  if (!designer) {
    return '';
  }

  return messages[status];
};
